"use client";

import { useState, useEffect } from 'react';
import { 
  ExclamationTriangleIcon, 
  XMarkIcon, 
  ChevronUpIcon, 
  ChevronDownIcon,
  InformationCircleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

export interface Warning {
  id: string;
  type: 'warning' | 'error' | 'info' | 'success';
  title: string;
  message: string;
  timestamp: Date;
  dismissible?: boolean;
}

interface WarningIndicatorProps {
  warnings: Warning[];
  onDismissWarning: (warningId: string) => void;
  onDismissAll: () => void;
}

export default function WarningIndicator({ 
  warnings, 
  onDismissWarning, 
  onDismissAll 
}: WarningIndicatorProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Show indicator if there are warnings
  useEffect(() => {
    setIsVisible(warnings.length > 0);
  }, [warnings.length]);

  if (!isVisible) return null;

  // Each notice keeps its own tone; informational notes never read as errors.
  const TONE: Record<Warning['type'], { token: string; Icon: typeof ExclamationTriangleIcon }> = {
    error: { token: 'critical', Icon: ExclamationTriangleIcon },
    warning: { token: 'warning', Icon: ExclamationTriangleIcon },
    info: { token: 'info', Icon: InformationCircleIcon },
    success: { token: 'positive', Icon: CheckCircleIcon },
  };
  const getWarningIcon = (type: Warning['type']) => {
    const { token, Icon } = TONE[type];
    return <Icon className="h-5 w-5 shrink-0" style={{ color: `var(--ss-${token})` }} aria-hidden="true" />;
  };
  const toneStyle = (type: Warning['type']) => ({ borderColor: `var(--ss-${TONE[type].token})`, background: 'var(--ss-surface)', color: 'var(--ss-text)' });

  const errorCount = warnings.filter(w => w.type === 'error').length;
  const warningCount = warnings.filter(w => w.type === 'warning').length;
  const infoCount = warnings.filter(w => w.type === 'info' || w.type === 'success').length;
  const summaryType: Warning['type'] = errorCount ? 'error' : warningCount ? 'warning' : 'info';
  const summary = [
    errorCount ? `${errorCount} problem${errorCount > 1 ? 's' : ''}` : null,
    warningCount ? `${warningCount} heads-up${warningCount > 1 ? 's' : ''}` : null,
    infoCount ? `${infoCount} note${infoCount > 1 ? 's' : ''}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Collapsed Indicator */}
      <div className="flex items-center justify-end space-x-2">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
          className="flex items-center space-x-2 rounded-control border px-3 py-2 shadow-e2"
          style={toneStyle(summaryType)}
        >
          {getWarningIcon(summaryType)}
          <span className="text-sm font-medium">{summary}</span>
          {isExpanded ? (
            <ChevronDownIcon className="h-4 w-4" />
          ) : (
            <ChevronUpIcon className="h-4 w-4" />
          )}
        </button>

        {warnings.length > 1 && (
          <button
            onClick={onDismissAll}
            className="px-2 py-2 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-control hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            title="Dismiss all warnings"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Expanded Warnings List */}
      {isExpanded && (
        <div className="mt-2 max-w-sm space-y-2">
          {warnings.map((warning) => (
            <div
              key={warning.id}
              className="rounded-control border border-l-4 p-3 shadow-e2"
              style={toneStyle(warning.type)}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-2 flex-1">
                  {getWarningIcon(warning.type)}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{warning.title}</div>
                    <div className="mt-1 text-xs text-[var(--ss-text-muted)]">{warning.message}</div>
                    <div className="mt-2 text-xs text-[var(--ss-text-muted)]">
                      {warning.timestamp.toLocaleTimeString()}
                    </div>
                  </div>
                </div>
                {warning.dismissible !== false && (
                  <button
                    onClick={() => onDismissWarning(warning.id)}
                    className="ml-2 p-1 hover:bg-black/10 dark:hover:bg-white/10 rounded transition-colors"
                    title="Dismiss warning"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
